import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCubes, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import VisualPickerModal from './VisualPickerModal';
import { APISearchVisuals, APIWebVisualization, APITranslate } from '../helper/APIFunctions';
import './ContentCreationWizard.css';

/** Same term extraction as ContentCreationWizard.fetchSlideSearchResults */
const getSearchTermFromVisualField = (visualRaw) => {
  if (!visualRaw || !String(visualRaw).trim()) return '';
  const match = String(visualRaw).match(/^(?:molecule|anatomy|sketchfab|imaging):\s*(.+)$/i);
  return match ? match[1].trim() : String(visualRaw).trim();
};

/** Strip label text if it was accidentally stored with the suggestion */
const stripSuggestionUiPrefix = (s) =>
  String(s)
    .replace(/^\s*AI\s+visual\s+suggestion\s*:\s*/i, '')
    .trim();

/**
 * Catalog search is English-oriented. Hebrew/CJK/Cyrillic/etc. should not be searched raw —
 * translate first (otherwise we burn the empty-result → translate path on text where
 * `toLowerCase()` and comparisons behave unexpectedly).
 */
function catalogSearchNeedsEnglishFirst(text) {
  const t = text.trim();
  if (!t) return false;
  if (/[\u0590-\u05FF]/.test(t)) return true; // Hebrew
  if (/[\u0600-\u06FF]/.test(t)) return true; // Arabic
  if (/[\u0400-\u04FF]/.test(t)) return true; // Cyrillic
  if (/[\u3040-\u30FF\u3400-\u9FFF\uF900-\uFAFF]/.test(t)) return true; // CJK
  if (/[\uAC00-\uD7AF]/.test(t)) return true; // Hangul
  return false;
}

const MAX_CHARS_FOR_TRANSLATE = 1500;

const clipForTranslate = (text) => {
  const t = text.trim();
  if (t.length <= MAX_CHARS_FOR_TRANSLATE) return t;
  return t.slice(0, MAX_CHARS_FOR_TRANSLATE);
};

/** Long English prose often ranks poorly; prefer first sentence for keyword search */
function shortenCatalogQuery(en) {
  const t = en.trim();
  if (t.length <= 280) return t;
  const parts = t.split(/(?<=[.!?])\s+/);
  const first = parts[0];
  if (first && first.length >= 24 && first.length <= 320) return first.trim();
  return t.slice(0, 280).trim();
}

/** If /translate fails (403 etc.), try any embedded Latin technical tokens for catalog search */
function extractEmbeddedLatinTerms(text) {
  if (!text || !String(text).trim()) return '';
  const matches = String(text).match(/[A-Za-z][A-Za-z0-9_.-]{2,}/g);
  if (!matches?.length) return '';
  const uniq = [...new Set(matches.map((m) => m))];
  return uniq.slice(0, 10).join(' ');
}

const TRANSLATE_UNAVAILABLE_NOTE =
  'Translation failed (server rejected the request — often HTTP 403 if POST /translate is not allowed for this environment). Non-Latin text cannot be matched automatically. Use “Choose from library” and search in English, or enable the translate endpoint for your API stage.';

const getFallbackSearchTerm = (section) => {
  const title = (section?.title || '').trim();
  const body = (section?.text || '').trim();
  if (title) return title;
  if (body) return body.slice(0, 500);
  return '';
};

/** Normalize /translate API payloads — response shape varies by endpoint version */
function extractTranslatedStrings(body) {
  if (body == null) return [];
  const raw = body?.data !== undefined ? body.data : body;
  if (typeof raw === 'object' && raw !== null && !Array.isArray(raw) && raw.translation != null) {
    return [String(raw.translation).trim()].filter(Boolean);
  }
  let arr = raw;
  if (!Array.isArray(arr)) {
    if (Array.isArray(raw?.translations)) arr = raw.translations;
    else if (Array.isArray(raw?.results)) arr = raw.results;
    else return [];
  }
  return arr
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (item?.translation != null) return String(item.translation).trim();
      if (item?.text != null) return String(item.text).trim();
      return '';
    })
    .filter(Boolean);
}

function firstTranslatedLine(body) {
  const lines = extractTranslatedStrings(body);
  if (lines.length > 0) return lines[0];
  if (body && typeof body === 'object') {
    const o = body.data !== undefined ? body.data : body;
    if (typeof o?.translatedText === 'string') return o.translatedText.trim();
    if (typeof o?.text === 'string') return o.text.trim();
    if (typeof o?.english === 'string') return o.english.trim();
    if (typeof body.translatedText === 'string') return body.translatedText.trim();
  }
  return '';
}

const getResultThumbnail = (item) => {
  if (!item) return null;
  if (item.thumbnail) return item.thumbnail;
  if (item.type === 'images') return item.url;
  if (item.type === 'pdb') return `https://cdn.rcsb.org/images/structures/${item.id.toLowerCase()}_assembly-1.jpeg`;
  if (item.type === 'pubchem') return `https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/cid/${item.id}/PNG?record_type=3d`;
  if (item.type === 'anatomy-set') return '/snorkle_vis_thumbnails/anatomy_set_thumb.png';
  return null;
};

const getResultTypeLabel = (item) => {
  const labels = {
    pdb: 'Molecule / PDB',
    pubchem: 'Small Molecule',
    models: '3D Model',
    'anatomy-set': 'Anatomy Viewer',
    snorkle: 'Snorkle Visual',
    images: 'Image',
  };
  return labels[item?.type] || item?.type || 'Visual';
};

/**
 * Interactive visual suggestions using the same search pipeline as
 * Content Creation Wizard → AI scrollytelling → Select visuals & continue.
 */
const ScrollytellingInteractiveMatchModal = ({
  show,
  onHide,
  section,
  userInfo,
  setVisCache,
  onVisualChosen,
  isOrg = false,
}) => {
  const [searchState, setSearchState] = useState({
    status: 'idle',
    results: [],
    englishSearchNote: null,
    librarySearchHint: null,
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [showAllResults, setShowAllResults] = useState(false);
  const [showOwnPicker, setShowOwnPicker] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const visualSuggestion = section?.visualSuggestion || '';
  const vtMatch = visualSuggestion.match(/^(molecule|anatomy|sketchfab|imaging):\s*(.+)$/i);
  const typeLabels = { molecule: 'Molecule / PDB', anatomy: 'Anatomy Viewer', sketchfab: '3D Model', imaging: 'Imaging' };

  const resetLocalState = useCallback(() => {
    setSearchState({
      status: 'idle',
      results: [],
      englishSearchNote: null,
      librarySearchHint: null,
    });
    setSelectedItem(null);
    setShowAllResults(false);
    setShowOwnPicker(false);
    setIsApplying(false);
  }, []);

  useEffect(() => {
    if (!show || !section) return;

    resetLocalState();

    let rawTerm = stripSuggestionUiPrefix(getSearchTermFromVisualField(visualSuggestion));
    if (!rawTerm) rawTerm = stripSuggestionUiPrefix(getFallbackSearchTerm(section));
    if (!rawTerm) {
      setSearchState({
        status: 'done',
        results: [],
        englishSearchNote: null,
        librarySearchHint: null,
      });
      return;
    }

    let cancelled = false;
    setSearchState({
      status: 'loading',
      results: [],
      englishSearchNote: null,
      librarySearchHint: null,
    });

    /**
     * Visual search is indexed for English queries. Translate to English and retry when the
     * first search is empty; parse translate responses robustly; fall back to translated section title.
     */
    const finishDone = (results, query, alreadyUsedEnglishFallback) => {
      setSearchState({
        status: 'done',
        results,
        englishSearchNote:
          alreadyUsedEnglishFallback && results.length > 0
            ? `Showing matches for English search: ${query}`
            : alreadyUsedEnglishFallback && results.length === 0
              ? `No catalog matches for “${query}”. Try “Choose from library” below — search is prefilled in English.`
              : null,
        librarySearchHint: alreadyUsedEnglishFallback ? query : null,
      });
    };

    const translateThenSearch = (text, markFallback, onGiveUp) => {
      const clipped = clipForTranslate(text || '');
      if (!clipped) {
        onGiveUp();
        return;
      }
      const onTranslateFailed = () => {
        if (cancelled) return;
        const latin = extractEmbeddedLatinTerms(clipped);
        if (latin.replace(/\s+/g, ' ').trim().length >= 4) {
          searchWithQuery(latin.trim(), markFallback);
          return;
        }
        onGiveUp();
      };
      APITranslate(
        [clipped],
        'en',
        (body) => {
          if (cancelled) return;
          const en = firstTranslatedLine(body);
          if (en) {
            searchWithQuery(shortenCatalogQuery(en), markFallback);
          } else {
            onTranslateFailed();
          }
        },
        onTranslateFailed
      );
    };

    /** After we already searched an English-translated main query, try section title once */
    let postEnglishTitleTried = false;

    const searchWithQuery = (query, alreadyUsedEnglishFallback) => {
      APISearchVisuals(
        query,
        (res) => {
          if (cancelled) return;
          const results = res?.sources || [];

          if (
            results.length === 0 &&
            alreadyUsedEnglishFallback &&
            !postEnglishTitleTried
          ) {
            const title = (section?.title || '').trim();
            if (
              title &&
              title.toLowerCase() !== query.trim().toLowerCase()
            ) {
              postEnglishTitleTried = true;
              translateThenSearch(title, true, () =>
                finishDone([], query, true)
              );
              return;
            }
          }

          if (
            results.length === 0 &&
            !alreadyUsedEnglishFallback &&
            query.trim().length >= 2
          ) {
            translateThenSearch(query, true, () => {
              if (cancelled) return;
              const title = (section?.title || '').trim();
              if (title && title.toLowerCase() !== query.trim().toLowerCase()) {
                translateThenSearch(title, true, () => {
                  if (!cancelled) {
                    finishDone([], query, false);
                  }
                });
              } else if (!cancelled) {
                finishDone([], query, false);
              }
            });
            return;
          }
          finishDone(results, query, alreadyUsedEnglishFallback);
        },
        () => {
          if (!cancelled) {
            setSearchState({
              status: 'error',
              results: [],
              englishSearchNote: null,
              librarySearchHint: null,
            });
          }
        }
      );
    };

    const finishTranslateBlocked = () => {
      if (cancelled) return;
      const hint =
        extractEmbeddedLatinTerms(rawTerm) ||
        extractEmbeddedLatinTerms((section?.title || '').trim()) ||
        extractEmbeddedLatinTerms((section?.text || '').slice(0, 600));
      setSearchState({
        status: 'done',
        results: [],
        englishSearchNote: TRANSLATE_UNAVAILABLE_NOTE,
        librarySearchHint: hint || null,
      });
    };

    if (catalogSearchNeedsEnglishFirst(rawTerm)) {
      translateThenSearch(rawTerm, true, () => {
        if (cancelled) return;
        const title = (section?.title || '').trim();
        if (title && title.toLowerCase() !== rawTerm.trim().toLowerCase()) {
          translateThenSearch(title, true, () => {
            if (!cancelled) finishTranslateBlocked();
          });
        } else if (!cancelled) {
          finishTranslateBlocked();
        }
      });
    } else {
      searchWithQuery(rawTerm, false);
    }

    return () => {
      cancelled = true;
    };
  }, [show, section, visualSuggestion, resetLocalState]);

  const createVisualFromItem = (item) => {
    return new Promise((resolve) => {
      const typeMap = {
        pdb: 'pdb',
        pubchem: 'pubchem',
        models: 'sketchfab',
        'anatomy-set': 'anatomy_set',
        snorkle: 'snorkle',
      };
      const resourceType = typeMap[item.type];
      if (!resourceType) {
        resolve(null);
        return;
      }
      APIWebVisualization(
        item.id,
        resourceType,
        item.title,
        item.description || '',
        (res) => resolve(res),
        () => resolve(null),
        isOrg
      );
    });
  };

  const handleConfirm = async () => {
    if (!selectedItem) return;
    setIsApplying(true);
    try {
      let visual = null;
      if (selectedItem.visualization_url) {
        visual = selectedItem;
      } else {
        visual = await createVisualFromItem(selectedItem);
      }
      if (!visual || !visual.visualization_url) {
        alert('Could not add this visual to your library. Try another result or pick from your library.');
        setIsApplying(false);
        return;
      }
      if (visual.id && setVisCache) {
        setVisCache((prev) => ({ ...prev, [visual.id]: visual }));
      }
      onVisualChosen(visual);
      resetLocalState();
      onHide();
    } finally {
      setIsApplying(false);
    }
  };

  const initialPickerQuery =
    searchState.librarySearchHint ||
    (vtMatch ? vtMatch[2].trim() : getSearchTermFromVisualField(visualSuggestion) || getFallbackSearchTerm(section));

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered scrollable className="wizard-modal">
        <Modal.Header closeButton>
          <Modal.Title>Find matching interactive</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ position: 'relative' }}>
          {isApplying && (
            <div className="wizard-generating-overlay" style={{ borderRadius: 12 }}>
              <Spinner animation="border" variant="primary" style={{ width: '2.5rem', height: '2.5rem' }} />
              <p className="mt-3 mb-0 fw-semibold text-primary">Adding visual…</p>
            </div>
          )}

          <div className="visual-picker-step" style={{ padding: 0 }}>
            <p className="text-muted small mb-3">
              Suggested matches use the same search as AI story suggestions in the content wizard (research-backed interactive visuals).
              Non-English text is translated to English when the translation API is available; if translation is blocked (e.g. HTTP 403), use “Choose from library” and search in English.
            </p>

            <div className="visual-picker-slide-info">
              {section?.title && <h6 className="mb-1">{section.title}</h6>}
              {section?.text && (
                <p className="mb-2" style={{ fontSize: '0.88rem', color: '#374151' }}>
                  {section.text.length > 400 ? `${section.text.slice(0, 400)}…` : section.text}
                </p>
              )}
              {visualSuggestion && (
                <p className="mb-2 fst-italic text-muted small">
                  <strong>AI visual suggestion:</strong> {visualSuggestion}
                </p>
              )}
              {vtMatch && (
                <div className="mb-0">
                  <span className="visual-type-badge">{typeLabels[vtMatch[1].toLowerCase()] || vtMatch[1]}</span>{' '}
                  <strong style={{ fontSize: '0.84rem' }}>{vtMatch[2].trim()}</strong>
                </div>
              )}
            </div>

            <div className="visual-picker-results mt-3">
              {searchState.status === 'done' && searchState.englishSearchNote && (
                <p className="small mb-2 text-muted">{searchState.englishSearchNote}</p>
              )}
              {searchState.status === 'loading' && (
                <div className="d-flex flex-column align-items-center py-4">
                  <Spinner animation="border" variant="primary" size="sm" />
                  <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.85rem' }}>
                    Searching for visuals…
                  </p>
                </div>
              )}
              {searchState.status === 'error' && (
                <p className="text-muted text-center py-3 mb-0" style={{ fontSize: '0.85rem' }}>
                  Could not load results. Try “Choose from library” below or try again later.
                </p>
              )}
              {searchState.status === 'done' && searchState.results.length === 0 && (
                <p className="text-muted text-center py-3 mb-0" style={{ fontSize: '0.85rem' }}>
                  No matching visuals found. Use “Choose from library” to pick or create one.
                </p>
              )}
              {searchState.status === 'done' && searchState.results.length > 0 && (() => {
                const visibleResults = showAllResults ? searchState.results : searchState.results.slice(0, 4);
                return (
                  <>
                    <div className="visual-picker-grid">
                      {visibleResults.map((item, i) => {
                        const thumb = getResultThumbnail(item);
                        const isSelected = selectedItem === item;
                        return (
                          <div
                            key={i}
                            className={`visual-picker-card${isSelected ? ' selected' : ''}`}
                            onClick={() => setSelectedItem(isSelected ? null : item)}
                          >
                            <div className="visual-picker-card-thumb">
                              {thumb ? (
                                <img src={thumb} alt={item.title || ''} />
                              ) : (
                                <div className="visual-picker-card-no-thumb">
                                  <FontAwesomeIcon icon={faCubes} />
                                </div>
                              )}
                            </div>
                            <div className="visual-picker-card-info">
                              <span className="visual-type-badge" style={{ fontSize: '0.62rem' }}>
                                {getResultTypeLabel(item)}
                              </span>
                              <p className="visual-picker-card-title">{item.title || '(Untitled)'}</p>
                            </div>
                            {isSelected && (
                              <div className="visual-picker-card-check">
                                <FontAwesomeIcon icon={faCheckCircle} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {searchState.results.length > 4 && (
                      <button
                        type="button"
                        className="visual-picker-show-more"
                        onClick={() => setShowAllResults((v) => !v)}
                      >
                        {showAllResults ? 'Show fewer' : `Show all ${searchState.results.length} results`}
                      </button>
                    )}
                  </>
                );
              })()}
            </div>

            {selectedItem?.visualization_url && (
              <div className="visual-picker-custom-pick mt-2">
                <div className="visual-picker-custom-thumb">
                  {selectedItem.thumbnail ? (
                    <img src={selectedItem.thumbnail} alt={selectedItem.title || ''} />
                  ) : (
                    <div className="visual-picker-card-no-thumb">
                      <FontAwesomeIcon icon={faCubes} />
                    </div>
                  )}
                </div>
                <div className="visual-picker-custom-info">
                  <span className="visual-type-badge" style={{ fontSize: '0.68rem' }}>From library</span>
                  <p className="mb-0" style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b' }}>
                    {selectedItem.title || '(Untitled)'}
                  </p>
                </div>
                <button
                  type="button"
                  className="visual-picker-custom-clear"
                  onClick={() => setSelectedItem(null)}
                >
                  ✕
                </button>
              </div>
            )}

            <div className="visual-picker-own-row">
              <span className="visual-picker-own-divider">or</span>
              <button type="button" className="visual-picker-own-btn" onClick={() => setShowOwnPicker(true)}>
                Choose or create my own visual
              </button>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={isApplying}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!selectedItem || isApplying} onClick={handleConfirm}>
            Use selected visual
          </Button>
        </Modal.Footer>
      </Modal>

      {showOwnPicker && (
        <VisualPickerModal
          show={showOwnPicker}
          onHide={() => setShowOwnPicker(false)}
          userInfo={userInfo}
          initialSearchQuery={(initialPickerQuery || '').trim()}
          defaultView={initialPickerQuery?.trim() ? 'search' : 'options'}
          onSelect={(vis) => {
            setSelectedItem(vis);
            setShowOwnPicker(false);
          }}
        />
      )}
    </>
  );
};

export default ScrollytellingInteractiveMatchModal;
